import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { delay, expand, filter, mergeMap } from 'rxjs/operators';
import { GatewayConnection } from '../data/gateway-connection';
import { NodeConnection } from '../data/node-connection';
import { Rpc } from '../rpc/rpc';
import { RpcWorkItemClient } from '../rpc/work-item/rpc-work-item-client';
import { RpcWorkItem } from '../rpc/work-item/rpc-work-item-model';
import { RpcWorkItemFindClient } from '../rpc/work-item-find/rpc-work-item-find-client';
import { RpcWorkItemFind } from '../rpc/work-item-find/rpc-work-item-find-model';
import { NotificationConnection } from './notification-connection';
import { NotificationState } from './notification-state';
import { WorkItemManager } from './work-item-manager';
import { WorkItemFindResult, WorkItemRequestType, WorkItemResult, WorkItemSubmitRequest } from './work-item-request';

/**
 * Work item connection to submit a powershell work item, and to query its state.
 */
export class WorkItemConnection {
    public workItemManager: WorkItemManager;

    /**
     * Initializes a new instance of the WorkItemConnection class.
     *
     * @param rpc the RPC.
     * @param gatewayConnection the gateway connection.
     * @param notificationConnection the notification connection.
     */
    constructor(
        private rpc: Rpc,
        gatewayConnection: GatewayConnection,
        nodeConnection: NodeConnection,
        notificationConnection: NotificationConnection) {
        if (notificationConnection.notificationManager) {
            this.workItemManager = new WorkItemManager(this.rpc, gatewayConnection, nodeConnection, notificationConnection);
        }
    }

    /**
     * Submit a work item either directly to NotificationManager or through RPC.
     *
     * @param nodeName the name of the node to submit the item against.
     * @param request the work item request.
     * @return Observable the observable of WorkItemResult object.
     */
    public submit(nodeName: string, request: WorkItemSubmitRequest): Observable<WorkItemResult> {
        const data: RpcWorkItem = <any>{
            ...request,
            ...{
                type: WorkItemRequestType.WorkItemSubmit,
                sourceName: MsftSme.self().Environment.name,
                nodeName: nodeName,
                timestamp: Date.now()
            }
        };

        if (this.workItemManager) {
            return this.workItemManager.submitWorkItem(data);
        }

        // configure PowerShell module name before sending to RPC.
        if (data.powerShellCommand && data.powerShellCommand.module == null && MsftSme.self().Init.powerShellModuleName) {
            data.powerShellCommand.module = MsftSme.self().Init.powerShellModuleName;
        }

        const interval = 100;
        return from(RpcWorkItemClient.submitOrQueryWorkItem(this.rpc, data))
            .pipe(
                expand((result) => {
                    if (result.id) {
                        return EMPTY;
                    }

                    return of(result)
                        .pipe(
                            delay(interval),
                            mergeMap(() => {
                                const pollingData = <RpcWorkItem>{
                                    type: WorkItemRequestType.StateQuery,
                                    sourceName: MsftSme.self().Environment.name,
                                    timestamp: Date.now(),
                                    sequenceId: result.sequenceId
                                };

                                return RpcWorkItemClient.submitOrQueryWorkItem(this.rpc, pollingData);
                            }));
                }),
                filter((result) => !!result.id));
    }

    /**
     * Query a work item either directly to NotificationManager or through RPC.
     *
     * @param id the ID of the work item.
     * @return Observable the observable of WorkItemResult object.
     */
    public query(id: string): Observable<WorkItemResult> {
        const data = <RpcWorkItem>{
            type: WorkItemRequestType.StateQuery,
            sourceName: MsftSme.self().Environment.name,
            timestamp: Date.now(),
            id: id
        };

        if (this.workItemManager) {
            return this.workItemManager.queryWorkItem(data);
        }

        return from(RpcWorkItemClient.submitOrQueryWorkItem(this.rpc, data));
    }

    /**
     * Find existing work item with state.
     *
     * @param nodeName the node name.
     * @param moduleName the module name.
     * @param typeId the type ID.
     * @return Observable<WorkItemFindResult> the observable of WorkItemFindResult.
     */
    public find(nodeName: string, moduleName: string, typeId: string): Observable<WorkItemFindResult> {
        const data = <RpcWorkItemFind>{
            sourceName: MsftSme.self().Environment.name,
            nodeName: nodeName,
            moduleName: moduleName,
            typeId: typeId
        };

        if (this.workItemManager) {
            return of<WorkItemFindResult>(this.workItemManager.notificationConnection.notificationManager.workItemFind(data));
        }

        return from(RpcWorkItemFindClient.workItemFind(this.rpc, data));
    }

    /**
     * Submit a work item either directly to NotificationManager or through RPC, and wait for completion.
     *
     * @param nodeName the name of the node to submit the work item against
     * @param request the work item request.
     * @param timeout the timeout milliseconds. (optional, default forever until unsubscribe)
     * @param interval the interval period milliseconds. (optional, default 1 sec)
     * @return Observable the observable of WorkItemResult object.
     */
    public submitAndWait(
            nodeName: string,
            request: WorkItemSubmitRequest,
            timeout: number = 0,
            interval: number = 1000): Observable<WorkItemResult> {
        return this.waitObservable(this.submit(nodeName, request), timeout, interval);
    }

    /**
     * Wait for existing work item with state.
     *
     * @param workItemResult the work item result to wait for the final result.
     * @param timeout the timeout milliseconds. (optional, default forever until unsubscribe)
     * @param interval the interval period milliseconds. (optional, default 1 sec)
     * @return Observable<WorkItemFindResult> the observable of WorkItemFindResult.
     */
    public wait(workItemResult: WorkItemResult, timeout: number = 0, interval: number = 1000): Observable<WorkItemResult> {
        if (!workItemResult) {
            return of(null);
        }

        return this.waitObservable(of(workItemResult), timeout, interval);
    }

    /**
     * Check if work item result was finished.
     *
     * @param result the work item result.
     * @return boolean true if work item was completed with success or error.
     */
    public isFinished(result: WorkItemResult): boolean {
        if (result.state !== NotificationState.Started && result.state !== NotificationState.InProgress) {
            return true;
        }

        return false;
    }

    /**
     * Wait for existing work item with state.
     *
     * @param workItemResult the work item result to wait for the final result.
     * @param timeout the timeout milliseconds. (optional, default forever until unsubscribe)
     * @param interval the interval period milliseconds. (optional, default 1 sec)
     * @return Observable<WorkItemFindResult> the observable of WorkItemFindResult.
     */
    private waitObservable(observable: Observable<any>, timeout: number = 0, interval: number = 1000): Observable<WorkItemResult> {
         return observable
            .pipe(
                expand((result, index) => {
                    if (this.isFinished(result)) {
                        return EMPTY;
                    } else if (timeout > 0 && interval * index > timeout) {
                        return throwError(() => new Error('timeout'));
                    }

                    return of(result)
                        .pipe(
                            delay(interval),
                            mergeMap(() => this.query(result.id)));
                }),
                filter((result) => this.isFinished(result)));
    }
}
